// Pure snapshot builder for SESSION booking price_snapshots — shared by the
// portal's manual (dealer/walk-up) path and the storefront checkout path.
//
// The output is the `price_snapshot` jsonb that create_manual_session_booking
// and hold_session_booking store. Shape confirmed against
// session_price_expected (migration 304):
//
//   snapshot = { mode:'session', riders:<int>, totalCents:<number>,
//                addons:[ { key, price_cents, per } ] }
//
//   totalCents = (flatRateCents ?? ladder[riders].total_cents)
//              + Σ addon.price_cents × (per === 'rider' ? riders : 1)
//
// Validation-boundary asymmetry (important): the STOREFRONT path
// (hold_session_booking) re-runs session_price_expected against the supplied
// snapshot and rejects a total/shape mismatch with `bad_price`. The MANUAL path
// (create_manual_session_booking — what this feeds) does NOT re-validate; it
// only range-checks `totalCents` (whole cents, 0–10,000,000) and TRUSTS the
// dealer's total (a dealer may enter a custom price). So THIS builder is the
// source of truth for the manual total — do not assume the DB will reject a bad
// one. We still emit the exact session_price_expected shape so the same snapshot
// round-trips through the storefront contract and reads consistently everywhere.
//
// mode/riders/totalCents are camelCase (read as `mode`, `riders`, `totalCents`).
// The addon element fields are snake_case (`key`, `price_cents`, `per`) — the
// shape session_price_expected matches against product.addons. Only
// key+price_cents+per are echoed; the product addon's `label` is UI-only, dropped.
//
// Pure + dependency-free so the DB-free unit suite covers it.

/** A complete party-pricing ladder entry (persisted shape, see sessionProductForm). */
export type SessionLadderEntry = { riders: number; total_cents: number }

/** A product addon definition (persisted shape). Only key/price_cents/per are echoed. */
export type SessionAddonDef = {
  key: string
  price_cents: number
  per: 'rider' | 'booking'
  // label/note/description may be present on the product addon; like label,
  // note (one-line helper text) and description (multi-paragraph customer
  // copy) are UI-only and never part of the snapshot.
  label?: string
  note?: string
  description?: string
}

/**
 * The snapshot echo of a selected addon (exactly what the RPC re-validates).
 * Internal to this module — the exported SessionSnapshot references it, but no
 * external consumer needs the element type by name (knip enforces this).
 */
type SessionSnapshotAddon = { key: string; price_cents: number; per: 'rider' | 'booking' }

/** The price_snapshot jsonb the create_manual_session_booking RPC expects. */
export type SessionSnapshot = {
  mode: 'session'
  riders: number
  totalCents: number
  addons: SessionSnapshotAddon[]
}

export type BuildSessionSnapshotInput = {
  riders: number
  /** The product's COMPLETE party-pricing ladder. */
  ladder: SessionLadderEntry[]
  /** The SELECTED addons (subset of the product's addons). */
  addons: SessionAddonDef[]
  /**
   * Dealer's custom total override (cents). When provided, it REPLACES the
   * computed total (mode/riders/addons unchanged). The manual RPC allows a
   * custom total without a ladder recompute, bounded only by its range guard.
   * 0 is a valid override (a comped session) — so guard on `undefined`, not
   * falsiness.
   */
  customCents?: number
  /**
   * Flat base price in cents (booking_products.flat_rate_cents). When set it
   * REPLACES the ladder lookup — a party product prices the same at every size,
   * so its ladder is empty by design and `ladder` may be []. Add-ons still layer
   * on top. Mirrors session_price_expected's flat-rate branch (migration 360).
   */
  flatRateCents?: number
}

/**
 * Build the price_snapshot for a manual session booking.
 *
 * ALWAYS returns the complete shape (never {} — decision 8A). Throws when the
 * rider count has no ladder entry AND no flat rate is given: the UI must
 * disable those rider counts, so reaching here with an unpriced count is a
 * bug, not a user path.
 */
export function buildSessionSnapshot(input: BuildSessionSnapshotInput): SessionSnapshot {
  const { riders, ladder, addons, customCents, flatRateCents } = input

  // Flat rate wins when set; only then may the ladder be empty.
  const baseCents =
    flatRateCents !== undefined ? flatRateCents : ladder.find((l) => l.riders === riders)?.total_cents
  if (baseCents === undefined) {
    throw new Error(`No party-pricing ladder entry for ${riders} rider(s)`)
  }

  const echoedAddons: SessionSnapshotAddon[] = addons.map((a) => ({
    key: a.key,
    price_cents: a.price_cents,
    per: a.per,
  }))

  const computedTotal =
    baseCents +
    echoedAddons.reduce(
      (sum, a) => sum + a.price_cents * (a.per === 'rider' ? riders : 1),
      0,
    )

  return {
    mode: 'session',
    riders,
    totalCents: customCents !== undefined ? customCents : computedTotal,
    addons: echoedAddons,
  }
}

/**
 * The rider counts a customer/dealer may pick for a slot: ladder entries that
 * fit the slot's remaining capacity, ascending. An unpriced count is never
 * offered. Shared by the storefront's session slot picker and the portal's
 * manual-booking modal (moved here so the two don't hand-copy the same rule).
 */
export function eligibleLadderEntries(
  ladder: SessionLadderEntry[],
  remaining: number,
): SessionLadderEntry[] {
  return [...ladder]
    .filter((l) => l.riders >= 1 && l.riders <= remaining)
    .sort((a, b) => a.riders - b.riders)
}

/**
 * The party sizes a customer/dealer may pick for a slot, as a flat rider-count
 * list.
 *
 * A ladder product only offers priced counts (eligibleLadderEntries). A
 * flat-rate product prices the same at every size and therefore has an EMPTY
 * ladder by design — reading eligibleLadderEntries for one would offer ZERO
 * sizes and silently block every booking. Its valid range is instead every
 * size from 1 up to rider_capacity, capped by what's actually left in the
 * slot. The headcount is never optional even here: it still drives waiver
 * slots and per-rider add-on pricing.
 */
export function eligiblePartySizes(
  ladder: SessionLadderEntry[],
  remaining: number,
  opts: { flatRate: boolean; riderCapacity: number },
): number[] {
  if (opts.flatRate) {
    const cap = Math.min(opts.riderCapacity, remaining)
    return cap >= 1 ? Array.from({ length: cap }, (_, i) => i + 1) : []
  }
  return eligibleLadderEntries(ladder, remaining).map((l) => l.riders)
}
